import React, { useEffect, useMemo, useState, useCallback } from "react";
import { testId } from '../../../../utils/testIds';
import { useController, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Icon from "../../../../icons";
import LoaderComponent from "../../../Loader";
import { useModules } from "../../../../utils/modules";
import UserImage from "../../../UserImage";
import { renderStaticImage } from "../../../../common/util";
import { sponsorSearch } from "./sponsorSearch";
import {
  ASSIGNMENT_TAB,
  setActiveTab,
  fetchTeamMembersRequest,
  fetchB2bListRequest,
  clearAssignmentMethod,
} from "../../../../containers/CreationForms/AddEditActivity/store/assignmentMethodSlice";
import {
  selectAssignmentMethodList,
  selectIsAssignmentMethodLoading,
  selectAssignmentActiveTab,
} from "../../../../containers/CreationForms/AddEditActivity/store/selectors";

const CONTRACT_TAB_ENABLED = false;

function tabToMethod(tab) {
  if (tab === ASSIGNMENT_TAB.TEAM_MEMBER) return "teamMember";
  if (tab === ASSIGNMENT_TAB.B2B) return "b2b";
  return "organization";
}

function idKeyForTab(tab) {
  if (tab === ASSIGNMENT_TAB.TEAM_MEMBER) return "UserId";
  if (tab === ASSIGNMENT_TAB.B2B) return "OrganizationId";
  return "OrgId";
}

function tabForMethod(method) {
  if (method === "b2b") return ASSIGNMENT_TAB.B2B;
  if (method === "organization") return ASSIGNMENT_TAB.CONTRACT;
  return ASSIGNMENT_TAB.TEAM_MEMBER;
}

function idsMatch(left, right) {
  if (left === undefined || left === null || right === undefined || right === null) {
    return false;
  }

  return String(left) === String(right);
}

function AssignmentMethodField({
  config,
  disabled: disabledProp,
  error,
}) {
  const {
    fieldName,
    fieldKey,
    label,
    isRequired,
    description,
  } = config || {};

  const defaultValue = config?.fieldSettings?.defaultValue;
  const isEdit = !!config?.fieldSettings?.isEdit;

  const name = fieldName || fieldKey || "assignTo";
  const { control } = useFormContext();
  const { field } = useController({ name, control, defaultValue });

  const dispatch = useDispatch();
  const { isModuleEnabled } = useModules();
  const list = useSelector(selectAssignmentMethodList);
  const loading = useSelector(selectIsAssignmentMethodLoading);
  const activeTab = useSelector(selectAssignmentActiveTab);

  const disabled = !!disabledProp || isEdit;
  const [tab, setTab] = useState(() => {
    const savedMethod = defaultValue?.assignment_method;
    if (savedMethod) return tabForMethod(savedMethod);
    return isEdit && activeTab ? activeTab : ASSIGNMENT_TAB.TEAM_MEMBER;
  });
  const [searchText, setSearchText] = useState("");
  const selectedAssignment = field.value;
  const selectedId = selectedAssignment?.assignMethodId;
  const selectedMethod = selectedAssignment?.assignment_method;

  useEffect(() => {
    if (isEdit && selectedMethod) {
      setTab(tabForMethod(selectedMethod));
    }
  }, [isEdit, selectedMethod]);

  useEffect(() => {
    if (!isEdit) dispatch(clearAssignmentMethod());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === ASSIGNMENT_TAB.TEAM_MEMBER) dispatch(fetchTeamMembersRequest({ page: 1 }));
    else if (tab === ASSIGNMENT_TAB.B2B) dispatch(fetchB2bListRequest({ page: 1 }));
    dispatch(setActiveTab(tab));
  }, [tab, dispatch]);

  const filtered = useMemo(
    () => sponsorSearch(list, searchText),
    [list, searchText],
  );

  const selectedItem = useMemo(() => {
    const idKey = idKeyForTab(tab);

    return list.find((item) => idsMatch(item[idKey], selectedId)) ?? null;
  }, [list, selectedId, tab]);

  const visibleItems = isEdit
    ? (selectedItem ? [selectedItem] : [])
    : filtered;

  const changeTab = (nextTab) => {
    if (disabled || loading) return;
    if (nextTab === ASSIGNMENT_TAB.CONTRACT && !CONTRACT_TAB_ENABLED) return;
    setTab(nextTab);
    setSearchText("");
    field.onChange(undefined);
  };

  const selectSponsor = (data) => {
    if (disabled) return;
    const key = idKeyForTab(tab);
    field.onChange({
      assignment_method: tabToMethod(tab),
      assignMethodId: data[key],
    });
  };

  const listHeader =
    tab === ASSIGNMENT_TAB.TEAM_MEMBER
      ? "List of team members"
      : "Suggested company(s) having effective B2B";

  const placeholder =
    tab === ASSIGNMENT_TAB.TEAM_MEMBER
      ? "Search by Name or Job title"
      : "Filter by Company name or Skillset or State or County";

  const renderRow = (item) => {
    if (tab === ASSIGNMENT_TAB.TEAM_MEMBER) {
      const active = idsMatch(selectedId, item.UserId);
      const fullName = `${item.FirstName || ""} ${item.LastName || ""}`.trim();
      return (
        <li
          
        >
          <div
          key={item.UserId}
          onClick={() => selectSponsor(item)}
          className={active ? "rowFlex userGroup active" : "rowFlex userGroup"}
          >
            <div className="userPic userPicIcon">
            <UserImage
              profileUrl={item.ProfileImage}
              size="32"
              profileName={fullName}
              className="user-avatar"
            />
            <span className={active ? 'badges profileChecked green' : ''}></span>
            </div>
            <div className="userInfo">
              {fullName}
              <label className="email">{item.Title}</label>
            </div>
          </div>
        </li>
      );
    }
    if (tab === ASSIGNMENT_TAB.B2B) {
      const active = idsMatch(selectedId, item.OrganizationId);
      return (
        <li
        >
          <div
          key={item.OrganizationId}
          onClick={() => selectSponsor(item)}
          className={active ? "rowFlex userGroup active" : "rowFlex userGroup"}
          >
           <div className="userPic userPicIcon">
            <img
              className="user-avatar"
              src={item.OrganizationImage || renderStaticImage('/images/default_org.png')}
              alt={item.OrganizationName}
              onError={(e) => { e.currentTarget.src = renderStaticImage('/images/default_org.png'); }}
            />
            <span className={active ? 'badges profileChecked green' : ''}></span>
            </div>
                      <div className="userInfo">
            {item.OrganizationName}
            <label>{item.Skillset || item.OrgState}</label>
          </div> 
          </div>
          

        </li>
      );
    }
    return null;
  };

  return (
    <div
      className={`formGroup activity-assignment-field ${
        disabled ? "activity-assignment-field--disabled" : ""
      }`}
    >
      <div className="wth-2">
        {label && (
          <label>
            {label}
            {isRequired ? <sup>*</sup> : null}
            {description && (
              <span
                className="quickTooltip-bottom"
                data-quicktooltip={description}
              >
                <Icon name="info" className="icon ml greyInfoIcon" />
              </span>
            )}
          </label>
        )}
      </div>
      <div className="wth-9 changeAssignee">
        <div className="tabNav mr ml">
          <div className="tabs">
            {isModuleEnabled("activities.activity_assignment_methods.internal") && <a
              className={`${tab === ASSIGNMENT_TAB.TEAM_MEMBER ? "active" : ""} ${
                disabled ? "disabled" : ""
              }`}
              onClick={() => changeTab(ASSIGNMENT_TAB.TEAM_MEMBER)}
            >
              Assign to a team member
            </a>}
            {isModuleEnabled("activities.activity_assignment_methods.b2b") && <a
              className={`${tab === ASSIGNMENT_TAB.B2B ? "active" : ""} ${
                disabled ? "disabled" : ""
              }`}
              onClick={() => changeTab(ASSIGNMENT_TAB.B2B)}
            >
              Assign to a B2B
            </a>}
            {isModuleEnabled("activities.activity_assignment_methods.contract") && <a
              className="disabled"
              aria-disabled="true"
              title="Coming soon"
              style={{ pointerEvents: "none", opacity: 0.5 }}
            >
              Assign to an active contract
            </a>}
          </div>

        </div>

        <div className="assignment-list-header rowFlex rowSpaceBetween">
          <div>{isEdit ? "Saved assignment" : listHeader}</div>
          {!isEdit && (
            <div className="assignment-search wth-4">
              <input
                data-test={testId('field', config.fieldKey, 'search')}
                type="text"
                placeholder={placeholder}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                disabled={disabled}
              />
            </div>
          )}
        </div>
          <div className="col-3">
        {isEdit ? (
          <ul className={`assignment-list wrapFlex ${disabled ? "disabled" : ""}`}>
            {loading ? (
              <li>
                <LoaderComponent />
              </li>
            ) : visibleItems.length === 0 ? (
              <li>Saved Item could not be found</li>
            ) : (
              visibleItems.map(renderRow)
            )}
          </ul>
        ) : (
          <ul className={`assignment-list wrapFlex ${disabled ? "disabled" : ""}`}>
            {loading ? (
              <li>
                <LoaderComponent />
              </li>
            ) : filtered.length === 0 ? (
              <li>No result found</li>
            ) : (
              filtered.map(renderRow)
            )}
          </ul>
        )}
        </div>

        {error ? (
          <span className="errorSelfMsg ml">{error}</span>
        ) : null}
      </div>
    </div>
  );
}

export default AssignmentMethodField;